import type { ReactNode } from 'react'

import { ChromeToolbarButton, ChromeToolbarDangerButton } from '../ChromeActions'
import { ChromeTooltip } from '../ChromeTooltip'
import {
  IconDownloadSmall,
  IconEyeSmall,
  IconLibraryAllFolders,
  IconPencil,
  IconPlusSmall,
  IconSelectionMode,
  IconSwapHalfHorizontal,
  IconTrash,
} from '../nav-icons'
import { comicSectionLabelClass } from './comicPanelTokens'

/** 漫画编辑左侧栏：区块分割线。统一为 margin: n 0，调用处不要补外边距。 */
export function ComicRailDivider({ className = '' }: { className?: string }) {
  return (
    <div
      role="presentation"
      className={['shell-rail-section-divider mx-0 my-4 shrink-0 border-b border-line', className].join(' ')}
    />
  )
}

export function ComicRailSection({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <section className={['min-w-0', className].join(' ')}>{children}</section>
}

export function ComicRailSectionHeader({
  title,
  actions,
}: {
  title: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-3 flex min-h-9 items-center justify-between gap-3">
      <p className={comicSectionLabelClass()}>{title}</p>
      {actions ? <div className="flex shrink-0 items-center justify-end gap-2">{actions}</div> : null}
    </div>
  )
}

export function ComicRailFullscreenButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconEyeSmall className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailDeleteButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarDangerButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconTrash className="h-4 w-4" aria-hidden />
    </ChromeToolbarDangerButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailDirectoryButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconLibraryAllFolders className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailEditButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconPencil className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailExportButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconDownloadSmall className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailMigrateButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconSwapHalfHorizontal className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailViewCandidatesButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
  highlight,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
  /** 待选定：强调提示 */
  highlight?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className={[
        'h-9 w-9 shrink-0 !gap-0 !p-0',
        highlight ? 'border-accent text-accent' : '',
      ].join(' ')}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconSelectionMode className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}

export function ComicRailAddButton({
  angular,
  label,
  tooltip,
  onClick,
  disabled,
}: {
  angular: boolean
  label: string
  tooltip: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <ChromeToolbarButton
      type="button"
      className="h-9 w-9 shrink-0 !gap-0 !p-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <IconPlusSmall className="h-4 w-4" aria-hidden />
    </ChromeToolbarButton>
  )

  return disabled ? button : (
    <ChromeTooltip content={tooltip} angular={angular} side="bottom" align="end">
      {button}
    </ChromeTooltip>
  )
}
